/**
 * App update store.
 *
 * Written by `<AppUpdateWatcher />`, read by the footer notice. Kept in a store
 * rather than component state so the mandatory countdown survives navigation:
 * the deadline is set once, when the update is first seen, and every later poll
 * leaves it alone. Restarting it on each poll would let someone sit on a stale
 * bundle indefinitely.
 */

import { create } from 'zustand';
import { getRunningVersion, isNewerVersion, type Release } from '@/config/releases';

/** How long someone gets to finish what they are doing before a forced reload. */
export const MANDATORY_GRACE_MS = 5 * 60 * 1000;

interface AppUpdateState {
  /** Version the server is currently serving, null until the first poll lands. */
  latestVersion: string | null;
  /** Releases newer than the running bundle, newest first. */
  pending: Release[];
  /** True when any pending release is flagged mandatory. */
  mandatory: boolean;
  /** Epoch ms at which a mandatory update reloads the page. */
  deadlineAt: number | null;
  modalOpen: boolean;

  applyManifest: (releases: Release[]) => void;
  openModal: () => void;
  closeModal: () => void;
  reset: () => void;
}

const EMPTY = {
  latestVersion: null,
  pending: [] as Release[],
  mandatory: false,
  deadlineAt: null,
  modalOpen: false,
};

export const useAppUpdateStore = create<AppUpdateState>((set) => ({
  ...EMPTY,

  applyManifest: (releases) =>
    set((state) => {
      // Resolved per poll rather than captured once, so the dev-only
      // ?pretendVersion override takes effect without a restart.
      const running = getRunningVersion();
      const pending = releases.filter((r) => isNewerVersion(r.version, running));
      const mandatory = pending.some((r) => r.mandatory === true);

      return {
        latestVersion: releases[0]?.version ?? state.latestVersion,
        pending,
        mandatory,
        // Start the clock the first time a mandatory update is seen and never
        // restart it, so polling cannot extend the grace period forever.
        deadlineAt: mandatory
          ? state.deadlineAt ?? Date.now() + MANDATORY_GRACE_MS
          : null,
        modalOpen: state.modalOpen,
      };
    }),

  openModal: () => set({ modalOpen: true }),
  closeModal: () => set({ modalOpen: false }),
  reset: () => set(EMPTY),
}));
